import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react'; // Import X icon
import { Modal, Box, } from '@mui/material';
import api from '../axios/api';
import LocationCustomCard from '../componentsII/LocationCard';
import PageHeader from "../componentsII/PageHeader";
import AddLocationModal from '../components/CompanyLocation/AddLocationModal';
import location from "../images/Location.jpg";
import LocationDetailModal from '../components/CompanyLocation/CompanyLocationDetailModal';
import { showNotification } from '../redux/slices/notificationSlice';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import GeoFencingModal from '../components/GeoFencingModal';
import SettingsModal from '../components/SettingsModal';

import { fetchCounts } from '../redux/slices/countsSlice';

const LocationSetting = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const [locationsData, setLocationsData] = useState([]);
  const [inactiveLocationsData, setInactiveLocationsData] = useState([]);
  const [activeLocationsData, setActiveLocationsData] = useState([]);

  const [geoFencingModalOpen, setGeoFencingModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const user = useSelector((state) => state.user.user);
  const year = useSelector((state) => state.user.selectedYear);
  
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [detailModalData, setDetailModalData] = useState({});
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const dispatch = useDispatch();

  const handleOpenSettingsModal = () => setIsSettingsModalOpen(true);
  const handleCloseSettingsModal = () => {fetchLocations();setIsSettingsModalOpen(false)};

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditData(null);
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await api.get(`/company/companylocation?company_id=${user.company.id}&year_id=${year.id}`);
      if (response.data.data) {
        const Locations = response.data.data.filter(item => item.is_active === 1);
        setLocationsData(Locations);

        const activeLocations = response.data.data.filter(item => item.is_active === 1).map(({ id, loc_name, city_name, ...rest }) => ({id,name: loc_name,details: city_name,...rest,}));
        const inactiveLocations = response.data.data.filter(item => item.is_active === 0).map(({ id, loc_name, city_name, ...rest }) => ({id,name: loc_name,details: city_name,...rest,}));
      
        setActiveLocationsData(activeLocations);
        setInactiveLocationsData(inactiveLocations);
      }
      dispatch(fetchCounts({
        companyId: user.company.id,
        yearId: year.id
      }));
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };


  const handleOpenDetailModal = () => setIsDetailModalOpen(true);
  const handleCloseDetailModal = () => setIsDetailModalOpen(false);

  const handleOpenGeoFencingModal = () => setGeoFencingModalOpen(true);
  const handleCloseGeoFencingModal = () => setGeoFencingModalOpen(false);

  return (
    <PageHeader
      title="Location Setting"
      text={[
        "Configure your company's fiscal years and set important targets for emissions reduction.",
        "Define your base year to establish a starting point for measuring progress and set target years with specific reduction goals.",
        "This data will be used throughout the system to track and visualize your company's environmental impact over time.",
      ]}
      buttonTitle="Add Location"
      handleClick={handleOpenModal}
      showBtn2={true}
      disableBtn2 = {!year.is_copied}
      handleBtn2Click={handleOpenSettingsModal}
      buttonTitle2='Copy Locations'
    >
      {locationsData.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-lg">
          <img
            src="/vectors/white isometric factory.jpg"
            alt="No Data"
            className="w-80 h-80 mx-auto text-gray-400 mb-4"
          />
          <h3 className="text-xl font-medium text-gray-600 mb-2">No Location Found</h3>
          <p className="text-gray-500">Add your first location to get started</p>
        </div>
      ) : (
        // Table
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto border grid grid-cols-4 gap-4 p-4">
            {locationsData.map((item) => {
              return (
                <LocationCustomCard
                  image={location}
                  title={item.loc_name}
                  subtitleI={`${item?.address_line_1}`}
                  subtitleII={item?.city_name}
                  onClick={() => {
                    handleOpenDetailModal();
                    setDetailModalData({ ...item, location });
                  }}
                  buttonTitle={item.geo_fence_latlong ? "Edit Geofence" : "Add Geofence"}
                  buttonClassName={item.geo_fence_latlong ? 'bg-green-600 hover:bg-green-700 text-white border border-green-500 font-medium w-full text-center flex justify-center hover:-translate-y-0' : 'bg-white-600 hover:bg-green-700 hover:text-white text-black border border-green-500 font-medium w-full text-center flex justify-center hover:-translate-y-0'}
                  clickFun={() => {
                    setGeoFencingModalOpen(true);
                    setDetailModalData({ ...item, location });
                  }}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Modal for Adding Location */}
      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="add-location-modal-title"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'auto',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 0,
            borderRadius: '8px',
            display: 'relative',
          }}
        >
          {/* Close Button */}
          <button
            onClick={handleCloseModal}
            className="absolute top-2 right-2 text-gray-600 hover:text-gray-800"
            aria-label="Close"
          >
            <X size={24} />
          </button>

          <AddLocationModal
            isOpen={isModalOpen}
            initialData={editData}
            mode={editData ? 'edit' : 'create'}
            onSubmit={(response) => {
              if (response.status === "200") {
                dispatch(
                  showNotification({
                    message: editData ? 'Location updated successfully!' : 'Location added successfully!',
                    type: "success",
                  })
                );
                handleCloseModal();
                fetchLocations();
              } else {
                dispatch(
                  showNotification({
                    message: response.data?.message || 'An error occurred',
                    type: "error",
                  })
                );
              }
            }}
          />
        </Box>
      </Modal>

      {/* detail modal */}
      <Modal
        open={isDetailModalOpen}
        onClose={handleCloseDetailModal}
        aria-labelledby="add-location-modal-title"
        aria-describedby="add-location-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "auto",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: "8px",
            display: "relative",
          }}
        >
          <button
            onClick={handleCloseDetailModal}
            className="absolute top-2 right-2 text-gray-600 hover:text-gray-800"
            aria-label="Close"
          >
            <X size={24} />
          </button>
          <LocationDetailModal detailModalData={detailModalData} refetch={fetchLocations} handleCloseMainDetailModal={handleCloseDetailModal} />
        </Box>
      </Modal>

      {/* GeofencingModal */}
      <Modal
        open={geoFencingModalOpen}
        onClose={handleCloseGeoFencingModal}
        aria-labelledby="add-location-modal-title"
        aria-describedby="add-location-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "auto",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: "8px",
            display: "relative",
          }}
        >
          <button
            onClick={handleCloseGeoFencingModal}
            className="absolute top-2 right-2 text-gray-600 hover:text-gray-800"
            aria-label="Close"
          >
            <X size={24} />
          </button>
          <GeoFencingModal ModalData={detailModalData} refetch={fetchLocations} handleCloseMainDetailModal={handleCloseGeoFencingModal} />
        </Box>
      </Modal>
      <Modal
        open={isSettingsModalOpen}
        onClose={handleCloseSettingsModal}
        aria-labelledby="add-location-modal-title"
        aria-describedby="add-location-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "auto",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: "8px",
            display: "relative",
          }}
        >
          <button
            onClick={handleCloseSettingsModal}
            className="absolute top-2 right-2 text-gray-600 hover:text-gray-800"
            aria-label="Close"
          >
            <X size={24} />
          </button>
          <SettingsModal
            ActiveList={activeLocationsData}
            InactiveList={inactiveLocationsData}
            HelperText={[`Below is the list of vehicles that are active for ${year.start_year} and the list of locations available from other reporting years but inactive for the current reporting year.`, "You can add new locations to this reporting year from your main locations page"]}
            ModalFor='Locations'
            Title='Configure Locations'
            EmptyActiveImage={"/vectors/white isometric factory.jpg"}
            EmptyInactiveImage={"/vectors/white isometric factory.jpg"}
            EmptyActiveText='No Active Locations'
            EmptyInactiveText='No Inactive Locations'
            handleCancel={handleCloseSettingsModal}
          />
        </Box>
      </Modal>
    </PageHeader>
  );
};

export default LocationSetting;
